import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import * as projectService from '../services/projectService';
import * as projectTypeService from '../services/projectTypeService';
import * as companyService from '../services/companyService';
import * as activityService from '../services/activityService';
import type { ProjectWithDetails, CreateProjectInput } from '../services/projectService';
import type { ProjectType } from '../services/projectTypeService';
import type { CompanyStats, PlatformStats } from '../services/companyService';
import type { Activity } from '../services/activityService';
import { SystemActivityFeed } from '../components/SystemActivityFeed';
import * as stakeholderApprovalService from '../services/stakeholderApprovalService';

/**
 * Dashboard Page
 *
 * Role-based dashboard that displays different content for each user type:
 * - Platform Admin (Super Admin)
 * - Company Admin
 * - Project Manager
 * - Contributor
 */

export function Dashboard(): React.ReactElement {
  const { user, company, token } = useAuth();
  const navigate = useNavigate();

  // Redirect taskboard_user to /taskboard
  useEffect(() => {
    if (user?.role === 'taskboard_user') {
      navigate('/taskboard', { replace: true });
    }
  }, [user, navigate]);
  const [projects, setProjects] = useState<ProjectWithDetails[]>([]);
  const [uniqueMemberCount, setUniqueMemberCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [companyStats, setCompanyStats] = useState<CompanyStats | null>(null);
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activityTimeFilter, setActivityTimeFilter] = useState<'day' | 'week'>('day');

  // Stakeholder pending approvals
  const [pendingApprovalCount, setPendingApprovalCount] = useState<number>(0);

  // Project search state
  const [projectSearchText, setProjectSearchText] = useState('');

  // Collapsible activity section
  const [isActivityExpanded, setIsActivityExpanded] = useState(() => {
    const saved = localStorage.getItem('dashboard_activity_expanded');
    return saved !== null ? saved === 'true' : true;
  });
  const toggleActivityExpanded = () => {
    const newState = !isActivityExpanded;
    setIsActivityExpanded(newState);
    localStorage.setItem('dashboard_activity_expanded', String(newState));
  };

  // Create Project Modal State
  const [isCreating, setIsCreating] = useState(false);
  const [modalError, setModalError] = useState('');
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [projectTypeSearch, setProjectTypeSearch] = useState('');
  const [createFormData, setCreateFormData] = useState<CreateProjectInput>({
    name: '',
    project_code: '',
    description: '',
    status: 'draft'
  });

  const loadProjects = async () => {
    if (!token) return;

    try {
      const { projects: userProjects, unique_member_count } = await projectService.listProjects({}, token);
      setProjects(userProjects);
      setUniqueMemberCount(unique_member_count || 0);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyStats = async () => {
    if (!token || !company?.id) return;

    setStatsLoading(true);
    try {
      const stats = await companyService.getCompanyStats(company.id, token);
      setCompanyStats(stats);
    } catch (error) {
      console.error('Error loading company stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadPlatformStats = async () => {
    if (!token) return;

    setStatsLoading(true);
    try {
      const stats = await companyService.getPlatformStats(token);
      setPlatformStats(stats);
    } catch (error) {
      console.error('Error loading platform stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadActivities = async () => {
    if (!token) return;

    setActivitiesLoading(true);
    try {
      // Use different endpoints based on role
      let activityList: Activity[];
      if (user?.role === 'project_manager' || user?.role === 'project_manager_lite') {
        // Project managers use the /my endpoint which returns activities they're involved in
        activityList = await activityService.getMyActivities({ limit: 50 }, token);
      } else {
        // Company admins use the company endpoint
        activityList = await activityService.getCompanyActivities({ limit: 50 }, token);
      }

      // Filter activities based on selected time range
      const now = new Date();
      let filtered = activityList.filter(activity => {
        const activityDate = new Date(activity.created_at);
        const diffTime = now.getTime() - activityDate.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        if (activityTimeFilter === 'day') {
          return diffDays <= 1;
        } else {
          return diffDays <= 7;
        }
      });

      // For project managers, filter to only their own projects
      if (user?.role === 'project_manager' || user?.role === 'project_manager_lite') {
        const projectIds = new Set(projects.map(p => p.id));
        filtered = filtered.filter(activity =>
          activity.project_id && projectIds.has(activity.project_id)
        );
      }

      setActivities(filtered);
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const loadPendingApprovalCount = async () => {
    if (!token) return;
    try {
      const count = await stakeholderApprovalService.getMyPendingCount(token);
      setPendingApprovalCount(count);
    } catch (error) {
      console.error('Error loading pending approval count:', error);
    }
  };

  useEffect(() => {
    loadProjects();
    // Load company stats and activities for Company Admin
    if (user?.role === 'company_admin') {
      loadCompanyStats();
      loadActivities();
    }
    // Load platform stats for Platform Admin
    if (user?.role === 'platform_admin') {
      loadPlatformStats();
    }
    // Load pending approval count for Stakeholder
    if (user?.role === 'stakeholder') {
      loadPendingApprovalCount();
    }
  }, [token, user?.role, company?.id]);

  // Load activities for Project Manager after projects are loaded
  useEffect(() => {
    if ((user?.role === 'project_manager' || user?.role === 'project_manager_lite') && projects.length > 0) {
      loadActivities();
    }
  }, [projects, user?.role]);

  // Reload activities when time filter changes
  useEffect(() => {
    if (user?.role === 'company_admin' || user?.role === 'project_manager' || user?.role === 'project_manager_lite') {
      loadActivities();
    }
  }, [activityTimeFilter]);

  // Handle Create Project Modal
  const handleCreateClick = async () => {
    if (!token) return;
    setModalError('');
    setProjectTypeSearch('');

    // Generate project code with placeholder name
    try {
      const generatedCode = await projectService.generateProjectCode(token, 'New Project');
      setCreateFormData({
        name: '',
        project_code: generatedCode,
        description: '',
        status: 'draft'
      });
      // Load project types
      const types = await projectTypeService.listProjectTypes({ is_active: true }, token);
      setProjectTypes(types);
      setIsCreating(true);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to generate project code');
    }
  };

  const handleCreateProject = async () => {
    if (!token) return;
    if (!createFormData.name) {
      setModalError('Please fill in all required fields');
      return;
    }

    try {
      await projectService.createProject(createFormData, token);
      setIsCreating(false);
      setModalError('');
      // Reload projects
      await loadProjects();
      // Navigate to projects page
      navigate('/projects');
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to create project');
    }
  };

  // Platform Admin Dashboard
  const renderPlatformAdminDashboard = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-white">Platform Administration</h1>
        <p className="mt-2 text-blue-100">System-wide overview and management</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">Total Companies</dt>
                  <dd className="text-2xl font-semibold text-text-primary">
                    {statsLoading ? '...' : platformStats?.total_companies || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">Total Users</dt>
                  <dd className="text-2xl font-semibold text-text-primary">
                    {statsLoading ? '...' : platformStats?.total_users || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">Total Projects</dt>
                  <dd className="text-2xl font-semibold text-text-primary">
                    {statsLoading ? '...' : platformStats?.total_projects || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">Total Requirements</dt>
                  <dd className="text-2xl font-semibold text-text-primary">
                    {statsLoading ? '...' : platformStats?.total_requirements || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Management and Activity Feed Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column (2/3) - System Management */}
        <div className="lg:col-span-2">
          <div className="bg-surface-primary shadow rounded-lg">
            <div className="px-6 py-5 border-b border-border-primary">
              <h3 className="text-lg font-medium text-text-primary">System Management</h3>
            </div>
            <div className="px-6 py-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <button
                  onClick={() => navigate('/projects/new')}
                  className="inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
                >
                  <svg className="mr-2 -ml-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  New Project
                </button>
                <button
                  onClick={() => navigate('/admin/system-health')}
                  className="inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
                >
                  <svg className="mr-2 -ml-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  System Health
                </button>
                <button
                  onClick={() => navigate('/admin/companies')}
                  className="inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
                >
                  <svg className="mr-2 -ml-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Manage Companies
                </button>
                <button
                  onClick={() => navigate('/users')}
                  className="inline-flex items-center justify-center px-4 py-3 border border-border-primary rounded-full shadow-sm text-sm font-medium text-text-secondary bg-surface-primary hover:bg-background-secondary dark:hover:bg-surface-secondary"
                >
                  <svg className="mr-2 -ml-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  View All Users
                </button>
                <button
                  onClick={() => navigate('/admin/jira-webhooks')}
                  className="inline-flex items-center justify-center px-4 py-3 border border-border-primary rounded-full shadow-sm text-sm font-medium text-text-secondary bg-surface-primary hover:bg-background-secondary dark:hover:bg-surface-secondary"
                >
                  <svg className="mr-2 -ml-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  JIRA Webhook Logs
                </button>
                <button
                  className="inline-flex items-center justify-center px-4 py-3 border border-border-primary rounded-full shadow-sm text-sm font-medium text-text-secondary bg-surface-primary hover:bg-background-secondary dark:hover:bg-surface-secondary opacity-50 cursor-not-allowed"
                  disabled
                >
                  <svg className="mr-2 -ml-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  System Settings
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right column (1/3) - Activity Feed */}
        <div className="lg:col-span-1">
          <SystemActivityFeed />
        </div>
      </div>
    </div>
  );

  // Company Admin Dashboard
  const renderCompanyAdminDashboard = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-white">Company Dashboard</h1>
        <p className="mt-2 text-blue-100">Overview of {company?.name}</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">Team Members</dt>
                  <dd className="text-2xl font-semibold text-text-primary">
                    {statsLoading ? '...' : companyStats?.total_users || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">Active Projects</dt>
                  <dd className="text-2xl font-semibold text-text-primary">
                    {statsLoading ? '...' : companyStats?.active_projects || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">Requirements</dt>
                  <dd className="text-2xl font-semibold text-text-primary">
                    {statsLoading ? '...' : companyStats?.total_requirements || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">Subscription</dt>
                  <dd className="text-sm font-semibold text-text-primary capitalize">{company?.subscription_tier}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid Layout: Main Content + Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Actions */}
          <div className="bg-surface-primary shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-text-primary mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => navigate('/projects/new')}
                className="flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-full text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
              >
                <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Project
              </button>
              <button
                onClick={() => navigate('/users')}
                className="flex items-center justify-center px-4 py-3 border border-border-primary text-sm font-medium rounded-full text-text-secondary bg-surface-primary hover:bg-background-secondary dark:hover:bg-surface-secondary"
              >
                <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Invite User
              </button>
              <button
                onClick={() => navigate('/company/settings')}
                className="flex items-center justify-center px-4 py-3 border border-border-primary text-sm font-medium rounded-full text-text-secondary bg-surface-primary hover:bg-background-secondary dark:hover:bg-surface-secondary"
              >
                <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Company Settings
              </button>
            </div>
          </div>

          {/* Recent Projects */}
          <div className="bg-surface-primary shadow rounded-lg">
            <div className="px-6 py-5 border-b border-border-primary">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-text-primary">Recent Projects</h3>
                <button
                  onClick={() => navigate('/projects')}
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
                >
                  View All
                </button>
              </div>
            </div>
            <div className="px-6 py-5">
              {loading ? (
                <p className="text-sm text-text-tertiary">Loading projects...</p>
              ) : projects.length === 0 ? (
                <p className="text-sm text-text-tertiary">No projects yet. Create your first project to get started!</p>
              ) : (
                <div className="space-y-3">
                  {projects.slice(0, 5).map((project) => (
                    <div
                      key={project.id}
                      onClick={() => navigate(`/projects/${project.id}`)}
                      className="flex items-center justify-between p-3 hover:bg-background-secondary dark:hover:bg-surface-secondary rounded-lg cursor-pointer"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-text-primary">{project.name}</p>
                        <p className="text-xs text-text-tertiary">{project.project_code}</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          project.status === 'done' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' :
                          project.status === 'draft' ? 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300' :
                          project.status === 'ready' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400' :
                          'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                        }`}>
                          {project.status === 'in_progress' ? 'In Progress' : project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                        </span>
                        <span className="text-xs text-text-tertiary">{project.member_count} members</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Activity Feed */}
        <div className="space-y-6">
          <div className="bg-surface-primary shadow rounded-lg p-6">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={toggleActivityExpanded}
            >
              <div className="flex items-center gap-2">
                <svg
                  className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${isActivityExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <h3 className="text-lg font-semibold text-text-primary">Recent Activity</h3>
              </div>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setActivityTimeFilter('day')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    activityTimeFilter === 'day'
                      ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
                      : 'bg-background-tertiary text-text-secondary hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => setActivityTimeFilter('week')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    activityTimeFilter === 'week'
                      ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
                      : 'bg-background-tertiary text-text-secondary hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  Week
                </button>
              </div>
            </div>
            {isActivityExpanded && (
              <div className="mt-4">
                {activitiesLoading ? (
                  <p className="text-sm text-text-tertiary">Loading activities...</p>
                ) : activities.length === 0 ? (
                  <p className="text-sm text-text-tertiary">No recent activity</p>
                ) : (
                  <div className="space-y-4">
                    {activities.map((activity) => {
                      // Determine icon based on activity type
                      const isUserActivity = activity.action_type === 'user_created';
                      const iconBgColor = isUserActivity ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-indigo-100 dark:bg-indigo-900/30';
                      const iconColor = isUserActivity ? 'text-blue-600 dark:text-blue-400' : 'text-indigo-600 dark:text-indigo-400';

                      return (
                        <div key={activity.id} className="pb-4 border-b border-border-primary last:border-0 last:pb-0">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <span className={`h-8 w-8 rounded-full ${iconBgColor} flex items-center justify-center`}>
                                {isUserActivity ? (
                                  <svg className={`h-4 w-4 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                ) : (
                                  <svg className={`h-4 w-4 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                )}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-text-primary">
                                {activity.user_first_name} {activity.user_last_name}
                              </p>
                              <p className="text-sm text-text-secondary">{activity.description}</p>
                              {activity.project_code && (
                                <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-background-tertiary text-text-primary">
                                  {activity.project_code}
                                </span>
                              )}
                              <p className="mt-1 text-xs text-text-tertiary">
                                {new Date(activity.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Project Manager Dashboard
  const renderProjectManagerDashboard = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-white">Project Manager Dashboard</h1>
        <p className="mt-2 text-blue-100">Manage your projects and teams</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">My Projects</dt>
                  <dd className="text-2xl font-semibold text-text-primary">{projects.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">In Progress</dt>
                  <dd className="text-2xl font-semibold text-text-primary">{projects.filter(p => p.status === 'in_progress').length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">Team Members</dt>
                  <dd className="text-2xl font-semibold text-text-primary">{uniqueMemberCount}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">Requirements</dt>
                  <dd className="text-2xl font-semibold text-text-primary">{projects.reduce((sum, p) => sum + p.requirement_count, 0)}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-surface-primary shadow rounded-lg">
        <div className="px-6 py-5 border-b border-border-primary">
          <h3 className="text-lg font-medium text-text-primary">Quick Actions</h3>
        </div>
        <div className="px-6 py-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <button
              onClick={() => navigate('/projects/new')}
              className="inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
            >
              <svg className="mr-2 -ml-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create New Project
            </button>
            <button
              onClick={() => navigate('/projects')}
              className="inline-flex items-center justify-center px-4 py-3 border border-border-primary rounded-full shadow-sm text-sm font-medium text-text-secondary bg-surface-primary hover:bg-background-secondary dark:hover:bg-surface-secondary"
            >
              <svg className="mr-2 -ml-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              View All Projects
            </button>
          </div>
        </div>
      </div>

      {/* Projects and Activity Feed Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* My Projects - Spans 2 columns */}
        <div className="lg:col-span-2">
          <div className="bg-surface-primary shadow rounded-lg">
            <div className="px-6 py-5 border-b border-border-primary">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-text-primary">My Projects</h3>
                <button
                  onClick={() => navigate('/projects')}
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
                >
                  View All
                </button>
              </div>
              {/* Search Input */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search projects by name, code, or description..."
                  value={projectSearchText}
                  onChange={(e) => setProjectSearchText(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-border-primary rounded-md leading-5 bg-surface-primary placeholder-text-tertiary focus:outline-none focus:placeholder-text-secondary focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-text-primary"
                />
                {projectSearchText && (
                  <button
                    onClick={() => setProjectSearchText('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg className="h-5 w-5 text-text-tertiary hover:text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="px-6 py-5">
              {loading ? (
                <p className="text-sm text-text-tertiary">Loading projects...</p>
              ) : (() => {
                // Filter projects based on search text
                const filteredProjects = projects.filter(project => {
                  if (!projectSearchText) return true;
                  const searchLower = projectSearchText.toLowerCase();
                  return (
                    project.name.toLowerCase().includes(searchLower) ||
                    project.project_code.toLowerCase().includes(searchLower) ||
                    (project.description && project.description.toLowerCase().includes(searchLower))
                  );
                });

                if (projects.length === 0) {
                  return (
                    <div className="text-center py-6">
                      <svg className="mx-auto h-12 w-12 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-text-primary">No projects</h3>
                      <p className="mt-1 text-sm text-text-tertiary">Get started by creating a new project.</p>
                      <div className="mt-6">
                        <button
                          onClick={() => navigate('/projects')}
                          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-full text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
                        >
                          <svg className="mr-2 -ml-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          New Project
                        </button>
                      </div>
                    </div>
                  );
                }

                if (filteredProjects.length === 0) {
                  return (
                    <div className="text-center py-6">
                      <svg className="mx-auto h-12 w-12 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-text-primary">No matching projects</h3>
                      <p className="mt-1 text-sm text-text-tertiary">Try adjusting your search terms.</p>
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {filteredProjects.map((project) => (
                    <div
                      key={project.id}
                      onClick={() => navigate(`/projects/${project.id}`)}
                      className="bg-surface-primary border border-border-primary rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="text-lg font-semibold text-text-primary line-clamp-1">{project.name}</h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            project.status === 'done' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' :
                            project.status === 'draft' ? 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300' :
                            project.status === 'ready' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400' :
                            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                          }`}>
                            {project.status === 'in_progress' ? 'In Progress' : project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                          </span>
                        </div>
                        <p className="text-sm text-text-secondary mb-4">{project.project_code}</p>
                        {project.description && (
                          <p className="text-sm text-text-secondary mb-4 line-clamp-2">{project.description}</p>
                        )}
                        <div className="flex items-center justify-between pt-4 border-t border-border-primary">
                          <div className="flex items-center text-sm text-text-secondary">
                            <svg className="mr-1.5 h-4 w-4 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span className="font-medium">{project.member_count}</span>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center text-sm">
                              <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-medium text-sm">
                                {project.requirement_count}
                              </div>
                            </div>
                          </div>
                        </div>
                        {project.target_completion_date && (
                          <div className="mt-3 pt-3 border-t border-border-primary flex items-center text-xs text-text-tertiary">
                            <svg className="mr-1.5 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Target Date: {new Date(project.target_completion_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Activity Feed - Spans 1 column */}
        <div className="lg:col-span-1">
          <div className="bg-surface-primary shadow rounded-lg">
            <div className="px-6 py-5 border-b border-border-primary">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={toggleActivityExpanded}
              >
                <div className="flex items-center gap-2">
                  <svg
                    className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${isActivityExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <h3 className="text-lg font-medium text-text-primary">My Projects Activity</h3>
                </div>
                <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setActivityTimeFilter('day')}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                      activityTimeFilter === 'day'
                        ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
                        : 'bg-background-tertiary text-text-secondary hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    Last 24h
                  </button>
                  <button
                    onClick={() => setActivityTimeFilter('week')}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                      activityTimeFilter === 'week'
                        ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
                        : 'bg-background-tertiary text-text-secondary hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    Last 7 days
                  </button>
                </div>
              </div>
            </div>
            {isActivityExpanded && (
              <div className="px-6 py-5">
                {activitiesLoading ? (
                  <p className="text-sm text-text-tertiary">Loading activities...</p>
                ) : activities.length === 0 ? (
                  <p className="text-sm text-text-tertiary text-center py-8">No recent activity</p>
                ) : (
                  <div className="flow-root">
                    <ul className="-mb-8">
                      {activities.slice(0, 10).map((activity, idx) => (
                        <li key={activity.id}>
                          <div className="relative pb-8">
                            {idx !== Math.min(activities.length, 10) - 1 && (
                              <span
                                className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-border-primary"
                                aria-hidden="true"
                              />
                            )}
                            <div className="relative flex space-x-3">
                              <div>
                                <span className="h-8 w-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center ring-8 ring-surface-primary">
                                  <svg className="h-4 w-4 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                  </svg>
                                </span>
                              </div>
                              <div className="flex min-w-0 flex-1 justify-between space-x-4">
                                <div>
                                  <p className="text-sm text-text-primary">{activity.description}</p>
                                  {activity.project_name && (
                                    <p className="mt-0.5 text-xs text-text-tertiary">
                                      Project: {activity.project_name}
                                    </p>
                                  )}
                                </div>
                                <div className="whitespace-nowrap text-right text-xs text-text-tertiary">
                                  <time dateTime={activity.created_at}>
                                    {new Date(activity.created_at).toLocaleDateString()}
                                  </time>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Contributor Dashboard
  const renderContributorDashboard = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-white">Welcome back, {user?.first_name}!</h1>
        <p className="mt-2 text-blue-100">Here's what you're working on</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">My Projects</dt>
                  <dd className="text-2xl font-semibold text-text-primary">{projects.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">My Tasks</dt>
                  <dd className="text-2xl font-semibold text-text-primary">--</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">Team Members</dt>
                  <dd className="text-2xl font-semibold text-text-primary">{uniqueMemberCount}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* My Projects */}
      <div className="bg-surface-primary shadow rounded-lg">
        <div className="px-6 py-5 border-b border-border-primary">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-text-primary">My Projects</h3>
            <button
              onClick={() => navigate('/projects')}
              className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
            >
              View All
            </button>
          </div>
        </div>
        <div className="px-6 py-5">
          {loading ? (
            <p className="text-sm text-text-tertiary">Loading projects...</p>
          ) : projects.length === 0 ? (
            <div className="text-center py-6">
              <svg className="mx-auto h-12 w-12 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-text-primary">No projects assigned</h3>
              <p className="mt-1 text-sm text-text-tertiary">You haven't been assigned to any projects yet. Contact your project manager to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {projects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="flex items-center justify-between p-4 hover:bg-background-secondary dark:hover:bg-surface-secondary rounded-lg cursor-pointer border border-border-primary"
                >
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h4 className="text-sm font-medium text-text-primary">{project.name}</h4>
                      <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        project.status === 'done' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' :
                        project.status === 'draft' ? 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300' :
                        project.status === 'ready' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400' :
                        'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                      }`}>
                        {project.status === 'in_progress' ? 'In Progress' : project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-text-tertiary">{project.project_code}</p>
                    <div className="mt-2 flex items-center space-x-4 text-xs text-text-tertiary">
                      <span className="flex items-center">
                        <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        {project.member_count} members
                      </span>
                      <span className="flex items-center">
                        <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                        {project.requirement_count} requirements
                      </span>
                    </div>
                  </div>
                  <div>
                    <svg className="h-5 w-5 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-surface-primary shadow rounded-lg">
        <div className="px-6 py-5 border-b border-border-primary">
          <h3 className="text-lg font-medium text-text-primary">Recent Activity</h3>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-text-tertiary">Activity feed coming soon</p>
        </div>
      </div>
    </div>
  );

  const renderStakeholderDashboard = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-white">Stakeholder Portal</h1>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">My Projects</dt>
                  <dd className="text-2xl font-semibold text-text-primary">{projects.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-orange-500 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-text-tertiary truncate">Pending Approvals</dt>
                  <dd className={`text-2xl font-semibold ${pendingApprovalCount > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-text-primary'}`}>{pendingApprovalCount}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* My Projects */}
      <div className="bg-surface-primary shadow rounded-lg">
        <div className="px-6 py-5 border-b border-border-primary">
          <h3 className="text-lg font-medium text-text-primary">My Projects</h3>
        </div>
        <div className="px-6 py-5">
          {loading ? (
            <p className="text-sm text-text-tertiary">Loading projects...</p>
          ) : projects.length === 0 ? (
            <div className="text-center py-6">
              <svg className="mx-auto h-12 w-12 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-text-primary">No projects assigned</h3>
              <p className="mt-1 text-sm text-text-tertiary">You haven't been assigned to any projects yet. Contact your project manager to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {projects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg cursor-pointer border border-blue-200 dark:border-blue-800"
                >
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h4 className="text-sm font-medium text-text-primary">{project.name}</h4>
                      <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        project.status === 'done' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' :
                        project.status === 'draft' ? 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300' :
                        project.status === 'ready' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400' :
                        'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                      }`}>
                        {project.status === 'in_progress' ? 'In Progress' : project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                      </span>
                    </div>
                    {project.description && (
                      <p className="mt-1 text-xs text-text-tertiary line-clamp-2">{project.description}</p>
                    )}
                    <div className="mt-2 flex items-center space-x-4 text-xs text-text-tertiary">
                      {project.start_date && (
                        <span className="flex items-center">
                          <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {new Date(project.start_date).toLocaleDateString()}
                        </span>
                      )}
                      {project.due_date && (
                        <span className="flex items-center">
                          <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Due: {new Date(project.due_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <svg className="h-5 w-5 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render appropriate dashboard based on user role
  const renderDashboard = () => {
    if (!user) return null;

    switch (user.role) {
      case 'platform_admin':
        return renderPlatformAdminDashboard();
      case 'company_admin':
        return renderCompanyAdminDashboard();
      case 'project_manager':
      case 'project_manager_lite':
        return renderProjectManagerDashboard();
      case 'contributor':
        return renderContributorDashboard();
      case 'stakeholder':
        return renderStakeholderDashboard();
      default:
        return (
          <div className="text-center py-12">
            <p className="text-gray-500">Unknown user role</p>
          </div>
        );
    }
  };

  return (
    <Layout>
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        {renderDashboard()}
      </div>

      {/* Create Project Modal */}
      {isCreating && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
              onClick={() => { setIsCreating(false); setModalError(''); }}
            />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-surface-primary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-text-primary mb-4">Create New Project</h3>
                {modalError && (
                  <div className="mb-4 p-4 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-800 dark:text-red-400">{modalError}</p>
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Project Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={createFormData.name}
                      onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md bg-surface-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="My Project"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Project Code</label>
                    <input
                      type="text"
                      value={createFormData.project_code}
                      readOnly
                      className="w-full px-3 py-2 border border-border-primary rounded-md bg-background-secondary text-text-secondary cursor-not-allowed"
                      placeholder="Auto-generated"
                    />
                    <p className="mt-1 text-xs text-text-tertiary">Automatically generated based on your company name</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
                    <textarea
                      value={createFormData.description}
                      onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-border-primary rounded-md bg-surface-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Project description..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Project Type</label>
                    <input
                      type="text"
                      value={projectTypeSearch}
                      onChange={(e) => setProjectTypeSearch(e.target.value)}
                      placeholder="Search project types..."
                      className="w-full px-3 py-2 mb-2 border border-border-primary rounded-md bg-surface-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <select
                      value={createFormData.project_type_id || ''}
                      onChange={(e) => setCreateFormData({ ...createFormData, project_type_id: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md bg-surface-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">-- Select a project type --</option>
                      {projectTypes
                        .filter(type =>
                          projectTypeSearch === '' ||
                          type.name.toLowerCase().includes(projectTypeSearch.toLowerCase()) ||
                          type.category.toLowerCase().includes(projectTypeSearch.toLowerCase())
                        )
                        .map((type) => (
                          <option key={type.id} value={type.id}>{type.name} ({type.category})</option>
                        ))}
                    </select>
                    <p className="mt-1 text-xs text-text-tertiary">
                      Optional: Search and select a project type
                      {projectTypeSearch && ` (${projectTypes.filter(type =>
                        projectTypeSearch === '' ||
                        type.name.toLowerCase().includes(projectTypeSearch.toLowerCase()) ||
                        type.category.toLowerCase().includes(projectTypeSearch.toLowerCase())
                      ).length} results)`}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Status</label>
                    <select
                      value={createFormData.status}
                      onChange={(e) => setCreateFormData({ ...createFormData, status: e.target.value as any })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md bg-surface-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Start Date</label>
                    <input
                      type="date"
                      value={createFormData.start_date || ''}
                      onChange={(e) => setCreateFormData({ ...createFormData, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md bg-surface-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Target Completion Date</label>
                    <input
                      type="date"
                      value={createFormData.target_completion_date || ''}
                      onChange={(e) => setCreateFormData({ ...createFormData, target_completion_date: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md bg-surface-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-background-secondary px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleCreateProject}
                  className="w-full inline-flex justify-center rounded-full border border-transparent shadow-sm px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-base font-medium text-white hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Create Project
                </button>
                <button
                  onClick={() => { setIsCreating(false); setModalError(''); }}
                  className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary text-base font-medium text-text-secondary hover:bg-background-secondary dark:hover:bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Dashboard;
